import { useState, useCallback } from 'react';
import { supabase } from '@/lib/api/supabase';
import type {
  AddonWaitlistEntry,
  WaitlistStatus,
} from '@/types';

export interface UseAddonWaitlistState {
  loading: boolean;
  error: string | null;
}

export interface UseAddonWaitlistReturn extends UseAddonWaitlistState {
  // User-facing operations
  joinWaitlist: (addonId: string, variantName?: string) => Promise<{ success: boolean; position?: number; error?: string }>;
  leaveWaitlist: (addonId: string, variantName?: string) => Promise<{ success: boolean; error?: string }>;
  getWaitlistPosition: (addonId: string, variantName?: string) => Promise<number | null>;
  getUserWaitlistEntry: (addonId: string, variantName?: string) => Promise<AddonWaitlistEntry | null>;
  getUserWaitlistEntries: () => Promise<AddonWaitlistEntry[]>;

  // Admin operations
  getAddonWaitlist: (addonId: string, variantName?: string) => Promise<AddonWaitlistEntry[]>;
  promoteFromWaitlist: (waitlistId: string) => Promise<{ success: boolean; error?: string }>;
  removeFromWaitlist: (waitlistId: string) => Promise<{ success: boolean; error?: string }>;

  // Stats
  getWaitlistCount: (addonId: string, variantName?: string) => Promise<number>;
  getTotalWaitlistCount: () => Promise<number>;
}

/**
 * Convert database row to AddonWaitlistEntry
 */
function dbToEntry(db: any): AddonWaitlistEntry {
  return {
    id: db.id,
    userId: db.user_id,
    addonId: db.addon_id,
    variantName: db.variant_name,
    position: db.position,
    joinedAt: db.joined_at,
    email: db.email,
    firstName: db.first_name,
    lastName: db.last_name,
    status: db.status as WaitlistStatus,
    promotedAt: db.promoted_at,
    invoiceSentAt: db.invoice_sent_at,
    confirmedAt: db.confirmed_at,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
  };
}

/**
 * Helper to build a query with optional variant_name filter.
 * When variantName is undefined, filters to variant_name IS NULL (the non-variant queue).
 */
function addVariantFilter(query: any, variantName?: string) {
  if (variantName !== undefined) {
    return query.eq('variant_name', variantName);
  }
  return query.is('variant_name', null);
}

/**
 * Hook for managing addon waitlist operations
 * Provides both user-facing and admin operations
 * Supports variant-specific queues
 */
export function useAddonWaitlist(): UseAddonWaitlistReturn {
  const [state, setState] = useState<UseAddonWaitlistState>({
    loading: false,
    error: null,
  });

  const setLoading = (loading: boolean) => setState(prev => ({ ...prev, loading }));
  const setError = (error: string | null) => setState(prev => ({ ...prev, error }));

  // ============================================================================
  // User Operations
  // ============================================================================

  const joinWaitlist = useCallback(async (addonId: string, variantName?: string): Promise<{ success: boolean; position?: number; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        return { success: false, error: 'You must be logged in to join the waitlist' };
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        return { success: false, error: 'Could not find your profile' };
      }

      const insertData: Record<string, any> = {
        user_id: user.id,
        addon_id: addonId,
        email: user.email,
        first_name: profile.first_name,
        last_name: profile.last_name,
      };

      if (variantName !== undefined) {
        insertData.variant_name = variantName;
      }

      const { data, error } = await supabase
        .from('addon_waitlist')
        .insert(insertData)
        .select('position')
        .single();

      if (error) {
        if (error.code === '23505') {
          return { success: false, error: 'You are already on the waitlist for this item' };
        }
        return { success: false, error: error.message };
      }

      return { success: true, position: data.position };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const leaveWaitlist = useCallback(async (addonId: string, variantName?: string): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        return { success: false, error: 'You must be logged in' };
      }

      let query = supabase
        .from('addon_waitlist')
        .delete()
        .eq('user_id', user.id)
        .eq('addon_id', addonId)
        .eq('status', 'waiting');

      query = addVariantFilter(query, variantName);

      const { error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const getWaitlistPosition = useCallback(async (addonId: string, variantName?: string): Promise<number | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      let query = supabase
        .from('addon_waitlist')
        .select('position')
        .eq('user_id', user.id)
        .eq('addon_id', addonId)
        .eq('status', 'waiting');

      query = addVariantFilter(query, variantName);

      const { data, error } = await query.single();

      if (error || !data) return null;
      return data.position;
    } catch {
      return null;
    }
  }, []);

  const getUserWaitlistEntry = useCallback(async (addonId: string, variantName?: string): Promise<AddonWaitlistEntry | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      let query = supabase
        .from('addon_waitlist')
        .select('*')
        .eq('user_id', user.id)
        .eq('addon_id', addonId)
        .in('status', ['waiting', 'promoted', 'invoiced']);

      query = addVariantFilter(query, variantName);

      const { data, error } = await query.single();

      if (error || !data) return null;
      return dbToEntry(data);
    } catch {
      return null;
    }
  }, []);

  const getUserWaitlistEntries = useCallback(async (): Promise<AddonWaitlistEntry[]> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('addon_waitlist')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'waiting')
        .order('joined_at', { ascending: true });

      if (error || !data) return [];
      return data.map(dbToEntry);
    } catch {
      return [];
    }
  }, []);

  // ============================================================================
  // Admin Operations
  // ============================================================================

  const getAddonWaitlist = useCallback(async (addonId: string, variantName?: string): Promise<AddonWaitlistEntry[]> => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('addon_waitlist')
        .select('*')
        .eq('addon_id', addonId)
        .in('status', ['waiting', 'promoted', 'invoiced'])
        .order('position', { ascending: true });

      // If variantName is provided, filter by it; otherwise show all variants
      if (variantName !== undefined) {
        query = addVariantFilter(query, variantName);
      }

      const { data, error } = await query;

      if (error) {
        setError(error.message);
        return [];
      }

      return data?.map(dbToEntry) || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const promoteFromWaitlist = useCallback(async (waitlistId: string): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .rpc('promote_addon_waitlist_user', {
          p_waitlist_entry_id: waitlistId,
        });

      if (error) {
        return { success: false, error: error.message };
      }

      const result = data as any;
      return { success: result.success, error: result.error };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const removeFromWaitlist = useCallback(async (waitlistId: string): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('addon_waitlist')
        .delete()
        .eq('id', waitlistId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // Stats
  // ============================================================================

  const getWaitlistCount = useCallback(async (addonId: string, variantName?: string): Promise<number> => {
    try {
      let query = supabase
        .from('addon_waitlist')
        .select('*', { count: 'exact', head: true })
        .eq('addon_id', addonId)
        .eq('status', 'waiting');

      if (variantName !== undefined) {
        query = addVariantFilter(query, variantName);
      }

      const { count, error } = await query;

      if (error) return 0;
      return count || 0;
    } catch {
      return 0;
    }
  }, []);

  const getTotalWaitlistCount = useCallback(async (): Promise<number> => {
    try {
      const { count, error } = await supabase
        .from('addon_waitlist')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'waiting');

      if (error) return 0;
      return count || 0;
    } catch {
      return 0;
    }
  }, []);

  return {
    ...state,
    joinWaitlist,
    leaveWaitlist,
    getWaitlistPosition,
    getUserWaitlistEntry,
    getUserWaitlistEntries,
    getAddonWaitlist,
    promoteFromWaitlist,
    removeFromWaitlist,
    getWaitlistCount,
    getTotalWaitlistCount,
  };
}
